import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, Minus, Pencil, Plus, Trash2, X } from 'lucide-react';
import type { RoleDef } from '../../types';
import { roleService } from '../../services/roleService';
import { PERMISSION_LABELS, type Permission } from '../../auth/permissions';
import { PermissionChecklist } from './PermissionChecklist';

const PERMISSIONS = Object.keys(PERMISSION_LABELS) as Permission[];

/**
 * Roles & Permissions — list + inline create, matching UserGroupsTab's own
 * list/detail pattern. Unlike groups, a role never needs a SEPARATE detail
 * fetch: `GET /roles` already returns every role's full permission list (see
 * RoleOut's own docstring on the backend) — there just aren't enough roles
 * for a summary/detail split to earn its keep the way Groups' member/module
 * lists did. The detail view below is handed the row it already has.
 *
 * Gated by SettingsPage via `can('roles:manage')`.
 */
export default function UserRolesTab() {
  const [roles, setRoles] = useState<RoleDef[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPermissions, setNewPermissions] = useState<string[]>([]);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = async () => {
    setLoadError(null);
    try {
      setRoles(await roleService.list());
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load roles.');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggleNewPermission = (permission: Permission) => {
    setNewPermissions((current) =>
      current.includes(permission)
        ? current.filter((entry) => entry !== permission)
        : [...current, permission],
    );
  };

  const resetCreateForm = () => {
    setCreating(false);
    setNewName('');
    setNewDescription('');
    setNewPermissions([]);
    setCreateError(null);
  };

  const createRole = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const created = await roleService.create({
        name,
        description: newDescription.trim(),
        permissions: newPermissions,
      });
      resetCreateForm();
      await load();
      setSelectedId(created.id);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Could not create this role.');
    } finally {
      setCreateBusy(false);
    }
  };

  const selectedRole = roles?.find((role) => role.id === selectedId) ?? null;

  if (selectedId && selectedRole) {
    return (
      <RoleDetailView
        role={selectedRole}
        onBack={() => setSelectedId(null)}
        onChanged={(patch) =>
          setRoles((current) =>
            current?.map((role) => (role.id === selectedId ? { ...role, ...patch } : role)) ?? current,
          )
        }
        onDeleted={() => {
          setSelectedId(null);
          void load();
        }}
      />
    );
  }

  if (loadError) {
    return (
      <p role="alert" className="rounded-lg bg-red-50 px-3 py-2.5 text-xs font-medium text-red-600">
        {loadError}
      </p>
    );
  }

  if (roles === null) {
    return <p className="py-10 text-center text-sm text-slate-400">Loading roles…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        {roles.map((role) => (
          <button
            key={role.id}
            type="button"
            onClick={() => !role.isSystem && setSelectedId(role.id)}
            className={`rounded-xl border border-slate-200 p-4 text-left transition ${
              role.isSystem ? 'cursor-default' : 'hover:border-blue-300 hover:bg-blue-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                  role.id === 'admin' ? 'bg-[#0F2942] text-white' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {role.name}
              </span>
              <span className="text-[11px] text-slate-400">
                {role.permissions.length} permissions
              </span>
              {role.isSystem && (
                <span className="text-[10px] uppercase tracking-wide text-slate-400">Built-in</span>
              )}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              {role.description || `${role.memberCount} member${role.memberCount === 1 ? '' : 's'}`}
            </p>
          </button>
        ))}
      </div>

      {/* ── Create a custom role ── */}
      {creating ? (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#0F2942]">Create role</h4>
            <button
              type="button"
              onClick={resetCreateForm}
              aria-label="Cancel"
              className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Name *</span>
            <input
              autoFocus
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="e.g. GIS Editor"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
            />
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Description</span>
            <input
              value={newDescription}
              onChange={(event) => setNewDescription(event.target.value)}
              placeholder="What this role is for"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
            />
          </label>

          <div>
            <span className="text-xs font-semibold text-slate-600">Permissions</span>
            <div className="mt-1">
              <PermissionChecklist selected={newPermissions} onToggle={toggleNewPermission} />
            </div>
          </div>

          {createError && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
              {createError}
            </p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void createRole()}
              disabled={!newName.trim() || createBusy}
              className="rounded-lg bg-[#2DD4BF] px-4 py-2 text-xs font-semibold text-[#0B1F38] transition hover:bg-[#5EEAD4] disabled:opacity-50"
            >
              {createBusy ? 'Creating…' : 'Create role'}
            </button>
            <button
              type="button"
              onClick={resetCreateForm}
              className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-white"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F2942] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#16395C]"
        >
          <Plus className="h-3.5 w-3.5" />
          Create role
        </button>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2.5 font-semibold">Permission</th>
              {roles.map((role) => (
                <th key={role.id} className="px-3 py-2.5 text-center font-semibold">
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSIONS.map((permission) => (
              <tr key={permission} className="border-t border-slate-100">
                <td className="px-3 py-2.5 text-xs text-slate-700">
                  {PERMISSION_LABELS[permission]}
                </td>
                {roles.map((role) => (
                  <td key={role.id} className="px-3 py-2.5 text-center">
                    {role.permissions.includes(permission) ? (
                      <Check className="mx-auto h-4 w-4 text-teal-600" aria-label="Allowed" />
                    ) : (
                      <Minus className="mx-auto h-4 w-4 text-slate-300" aria-label="Not allowed" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-[11px] leading-relaxed text-slate-500">
        Admin and User are built-in and cannot be renamed, re-permissioned, or deleted. Every
        permission here is also enforced by the server, not only by what this page hides.
      </p>
    </div>
  );
}

function RoleDetailView({
  role,
  onBack,
  onChanged,
  onDeleted,
}: {
  role: RoleDef;
  onBack: () => void;
  /** Keeps the list's own copy in step without a full re-fetch. */
  onChanged: (patch: Partial<RoleDef>) => void;
  onDeleted: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(role.name);
  const [descriptionDraft, setDescriptionDraft] = useState(role.description);
  const [permissions, setPermissions] = useState<string[]>(role.permissions);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    descriptionDraft !== role.description ||
    permissions.length !== role.permissions.length ||
    role.permissions.some((permission) => !permissions.includes(permission));

  const togglePermission = (permission: Permission) => {
    setPermissions((current) =>
      current.includes(permission)
        ? current.filter((entry) => entry !== permission)
        : [...current, permission],
    );
  };

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name || name === role.name) {
      setRenaming(false);
      setNameDraft(role.name);
      return;
    }
    try {
      const updated = await roleService.update(role.id, { name });
      onChanged({ name: updated.name });
      setRenaming(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not rename this role.');
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await roleService.update(role.id, {
        description: descriptionDraft,
        permissions,
      });
      onChanged({ description: updated.description, permissions: updated.permissions });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save this role.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete the role "${role.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await roleService.remove(role.id);
      onDeleted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not delete this role.');
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to all roles
        </button>
        <button
          type="button"
          onClick={() => void remove()}
          disabled={deleting}
          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-1.5 text-[11.5px] font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {deleting ? 'Deleting…' : 'Delete role'}
        </button>
      </div>

      <div>
        {renaming ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void saveName();
                if (event.key === 'Escape') {
                  setNameDraft(role.name);
                  setRenaming(false);
                }
              }}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-base font-semibold text-slate-900 outline-none focus:border-teal-400"
            />
            <button
              type="button"
              onClick={() => void saveName()}
              aria-label="Save name"
              className="rounded-lg p-1.5 text-teal-600 hover:bg-teal-50"
            >
              <Check className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setRenaming(true)}
            className="group flex items-center gap-1.5 text-base font-semibold text-slate-900"
          >
            {role.name}
            <Pencil className="h-3.5 w-3.5 text-slate-300 opacity-0 transition group-hover:opacity-100" />
          </button>
        )}
        <p className="mt-1 text-[11px] text-slate-400">
          {role.memberCount} member{role.memberCount === 1 ? '' : 's'}
        </p>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {error}
        </p>
      )}

      <label className="block">
        <span className="text-xs font-semibold text-slate-600">Description</span>
        <input
          value={descriptionDraft}
          onChange={(event) => setDescriptionDraft(event.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
        />
      </label>

      <div>
        <span className="text-xs font-semibold text-slate-600">Permissions ({permissions.length})</span>
        <div className="mt-1">
          <PermissionChecklist selected={permissions} onToggle={togglePermission} />
        </div>
      </div>

      <button
        type="button"
        onClick={() => void save()}
        disabled={!dirty || saving}
        className="rounded-lg bg-[#0F2942] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
      >
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}
